import type {
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    InfiniteQueryConfigOptions,
    QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { v4 as uuid } from 'uuid';

import type { IRootState } from '../../../redux/core-store';
import { getLogger } from '../../../utils/logger';
import { baseApi } from '../../base-api';
import {
    ApiTagTypes,
    IGetConversationByIdParams,
    IGetMessagesV2Response,
    IMessage,
    IMessageEvent,
    IMessageParamsBase,
    ISendMessageBody,
    ISendMessageParams,
    MessageRole,
    MessageStageType,
    StreamEventType,
    StreamFailureReason,
    StreamStatus,
} from '../../types';
import { stream } from '../utils/stream';

import { insertMessages } from './cache-updaters';
import { CONVERSATION_FUNC, CONVERSATION_GET_MESSAGES_FUNC, MESSAGES_PER_PAGE } from './consts';
import { conversationEndpoints } from './conversation-endpoints';
import { onAbortEvent, onCloseEvent, onErrorEvent, onMessageEvent } from './events';
import { createConversationPlaceholder, getTurnInitialTurnMessages } from './utils';

export type GetMessagesPaginateParams = {
    limit: number;
    cursor?: string | null;
};

const logger = getLogger('conversationEndpoints');
export const chatUrl = `${process.env.REACT_APP_CHAT_SERVICE_URL}/conversations`;
export const chatV2Url = `${process.env.REACT_APP_CHAT_SERVICE_URL}/v2/conversations`;

const baseApiWithTags = baseApi.enhanceEndpoints({
    addTagTypes: [ApiTagTypes.CONVERSATION, ApiTagTypes.CONVERSATIONS_HISTORY],
});

export const acceptedStreamEvents = [
    StreamEventType.CHUNK,
    StreamEventType.TOOL_CALL,
    StreamEventType.TOOL_RESULT,
    StreamEventType.INPUT,
    StreamEventType.AUTOMATIC_ASSISTANT_MESSAGE,
];

const messagesInfiniteQueryOptions: InfiniteQueryConfigOptions<
    IGetMessagesV2Response,
    GetMessagesPaginateParams,
    IGetConversationByIdParams
> = {
    initialPageParam: {
        limit: MESSAGES_PER_PAGE,
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const cursor = lastPage?.cursor ?? null;
        const limit = lastPageParam.limit;

        if (!cursor) {
            return undefined;
        }

        return {
            limit,
            cursor,
        };
    },
};

export const messageEndpoints = baseApiWithTags.injectEndpoints({
    endpoints: (builder) => ({
        [CONVERSATION_GET_MESSAGES_FUNC]: builder.infiniteQuery<
            IGetMessagesV2Response,
            IGetConversationByIdParams,
            GetMessagesPaginateParams
        >({
            infiniteQueryOptions: messagesInfiniteQueryOptions,
            queryFn: async ({ queryArg, pageParam }, _queryApi, _extraOptions, baseQuery) => {
                const { customer_project_id, conversation_id } = queryArg;
                const { limit, cursor } = pageParam;

                const params = {
                    customer_project_id,
                    limit,
                    ...(cursor && { cursor }),
                };

                const response = await baseQuery({
                    url: `${chatV2Url}/${conversation_id}/messages`,
                    params,
                    method: 'GET',
                });

                const { error, data } = response as QueryReturnValue<
                    IGetMessagesV2Response,
                    FetchBaseQueryError,
                    FetchBaseQueryMeta
                >;

                if (error) {
                    return { error };
                }

                return { data };
            },
        }),
        sendMessage: builder.query<IMessage | null, ISendMessageParams>({
            queryFn: () => ({ data: null }),
            providesTags: [ApiTagTypes.CONVERSATION],
            async onCacheEntryAdded(
                args: ISendMessageParams,
                { cacheDataLoaded, cacheEntryRemoved, getState, dispatch }
            ) {
                await cacheDataLoaded;
                const {
                    customer_project_id,
                    conversation_id,
                    turn_id,
                    controller,
                    content,
                    lang_code,
                    selected_sources,
                    selected_smart_filters_ids,
                    filters,
                    upsert_conversation,
                    boost_mode,
                } = args;

                const state = getState() as IRootState;
                const {
                    auth: { token },
                    settings: { debugMode },
                } = state;

                if (!token) {
                    logger.warn('No token, will NOT create a Stream');
                    return;
                }

                if (!conversation_id) {
                    logger.warn('No conversation_id');
                    return;
                }

                if (!customer_project_id) {
                    logger.warn('No customer_project_id');
                    return;
                }

                const messages = getTurnInitialTurnMessages({ userInitialMessage: content, turnId: turn_id });

                const newConversation = createConversationPlaceholder({
                    conversationId: conversation_id,
                    messages,
                });

                if (upsert_conversation) {
                    dispatch(
                        conversationEndpoints.util.updateQueryData(
                            'getAllConversations',
                            { customer_project_id },
                            (draft) => {
                                if (draft.length) {
                                    draft.unshift(newConversation);
                                }
                            }
                        )
                    );
                    dispatch(
                        conversationEndpoints.util.upsertQueryData(
                            CONVERSATION_FUNC,
                            { customer_project_id, conversation_id },
                            newConversation
                        )
                    );
                    dispatch(
                        messageEndpoints.util.upsertQueryData(
                            CONVERSATION_GET_MESSAGES_FUNC,
                            { customer_project_id, conversation_id },
                            {
                                pages: [{ messages: newConversation.messages, cursor: null }],
                                pageParams: [{ limit: MESSAGES_PER_PAGE, cursor: undefined }],
                            }
                        )
                    );
                } else {
                    dispatch(
                        messageEndpoints.util.updateQueryData(
                            CONVERSATION_GET_MESSAGES_FUNC,
                            { customer_project_id, conversation_id },
                            (draft) => {
                                insertMessages(draft, messages);
                            }
                        )
                    );
                }

                const onError = onErrorEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onAbort = onAbortEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onClose = onCloseEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onEvent = onMessageEvent(
                    dispatch,
                    onError,
                    customer_project_id,
                    conversation_id,
                    messageEndpoints
                );

                const url = new URL(`${chatUrl}/${conversation_id}/messages`);
                url.searchParams.append('customer_project_id', customer_project_id);

                const body: ISendMessageBody = {
                    content,
                    lang_code,
                    selected_sources,
                    selected_smart_filters_ids,
                    filters,
                    turn_id,
                    debug: debugMode,
                    upsert_conversation,
                    boost_mode,
                };

                const signal = controller.signal;
                signal.addEventListener('abort', onAbort);

                stream<ISendMessageBody, IMessageEvent>({
                    url,
                    body,
                    abortController: controller,
                    acceptedEvents: acceptedStreamEvents,
                    onEvent,
                    onClose,
                    dispatch,
                    getState,
                }).catch((error) =>
                    onError({
                        error: JSON.stringify(error),
                        status: StreamStatus.FAILED,
                        reason: StreamFailureReason.UNKNOWN,
                    })
                );

                await cacheEntryRemoved;
                signal.removeEventListener('abort', onAbort);
            },
        }),
        retryMessage: builder.mutation<IMessage | null, IMessageParamsBase>({
            queryFn: () => ({ data: null }),
            async onCacheEntryAdded(
                args: IMessageParamsBase,
                { cacheDataLoaded, cacheEntryRemoved, getState, dispatch }
            ) {
                await cacheDataLoaded;
                const {
                    customer_project_id,
                    conversation_id,
                    controller,
                    lang_code,
                    selected_sources,
                    selected_smart_filters_ids,
                    filters,
                    turn_id,
                } = args;

                const state = getState() as IRootState;
                const {
                    auth: { token },
                    settings: { debugMode },
                } = state;

                if (!token) {
                    logger.warn('No token, will NOT create a Stream');
                    return;
                }

                if (!conversation_id) {
                    logger.warn('No conversation_id');
                    return;
                }

                if (!customer_project_id) {
                    logger.warn('No customer_project_id');
                    return;
                }

                dispatch(
                    messageEndpoints.util.updateQueryData(
                        CONVERSATION_GET_MESSAGES_FUNC,
                        { customer_project_id, conversation_id },
                        (draft) => {
                            insertMessages(draft, [
                                // Placeholder for the retried assistant message
                                {
                                    message_id: uuid(),
                                    turn_id,
                                    content: '',
                                    is_placeholder: true,
                                    role: MessageRole.ASSISTANT,
                                    status: StreamStatus.RUNNING,
                                    stage: MessageStageType.LOADING,
                                },
                            ]);
                        }
                    )
                );

                const onError = onErrorEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onAbort = onAbortEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onClose = onCloseEvent(dispatch, customer_project_id, conversation_id, messageEndpoints);

                const onEvent = onMessageEvent(
                    dispatch,
                    onError,
                    customer_project_id,
                    conversation_id,
                    messageEndpoints
                );

                const url = new URL(`${chatUrl}/${conversation_id}/messages/retry`);
                url.searchParams.append('customer_project_id', customer_project_id);

                const body: Partial<ISendMessageBody> = {
                    lang_code,
                    selected_sources,
                    selected_smart_filters_ids,
                    filters,
                    debug: debugMode,
                    turn_id,
                };

                const signal = controller.signal;
                signal.addEventListener('abort', onAbort);

                stream<Partial<ISendMessageBody>, IMessageEvent>({
                    url,
                    body,
                    abortController: controller,
                    acceptedEvents: acceptedStreamEvents,
                    onEvent,
                    onClose,
                    dispatch,
                    getState,
                }).catch((error) =>
                    onError({
                        error: JSON.stringify(error),
                        status: StreamStatus.FAILED,
                        reason: StreamFailureReason.UNKNOWN,
                    })
                );

                await cacheEntryRemoved;
                signal.removeEventListener('abort', onAbort);
            },
        }),
    }),
});

export const { useLazySendMessageQuery, useRetryMessageMutation, useGetMessagesInfiniteQuery } = messageEndpoints;
